<template>
  <transition name="modal">
    <div
      v-if="open"
      class="fixed inset-0 z-[100] flex items-center justify-center p-4 font-NotoSerifKhmer"
    >
      <!-- Backdrop -->
      <div
        class="absolute inset-0 bg-black/60 backdrop-blur-md"
        @click="handleClose('close')"
      ></div>

      <!-- Modal Content -->
      <div
        class="relative w-full max-w-3xl max-h-[90vh] overflow-y-auto bg-white/10 backdrop-blur-2xl border border-white/20 rounded-3xl shadow-[0_8px_32px_rgba(0,0,0,0.3)] p-6 md:p-8 animate-scale-up custom-scrollbar"
      >
        <!-- Header -->
        <div class="flex items-center justify-between mb-8">
          <div class="flex items-center gap-4">
            <div
              class="flex items-center justify-center w-12 h-12 rounded-full bg-white/10 border border-white/10 shadow-inner backdrop-blur-md text-emerald-400"
            >
              <i class="fa-solid fa-layer-group text-xl"></i>
            </div>
            <div class="flex flex-col">
              <h2 class="text-2xl font-bold text-white tracking-tight">
                {{ isEditDoc ? t("Edit Event") : t("Create New Event") }}
              </h2>
              <span class="text-sm text-white/50 font-medium"
                >{{ t("Manage your event details") }}</span
              >
            </div>
          </div>

          <button
            @click="handleClose('close')"
            class="text-white/40 hover:text-white transition-colors bg-white/5 hover:bg-white/10 p-2.5 rounded-full"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <!-- Form -->
        <form @submit.prevent="handleSubmit" class="space-y-6">
          <!-- Layout Grid -->
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-x-6 gap-y-6">
            <!-- Name Field -->
            <div class="space-y-2">
              <label for="name" class="text-sm font-medium text-white/80 ml-1">
                {{ t("Event Name") }} <span class="text-red-400">*</span>
              </label>
              <div class="relative group">
                <input
                  id="name"
                  v-model="name"
                  type="text"
                  :placeholder="t('Ex. Wedding Party')"
                  class="glass-input w-full pl-5 pr-4 py-3.5 rounded-2xl outline-none transition-all duration-300"
                  :class="
                    isExistEventName ? 'border-red-500/50 bg-red-500/10' : ''
                  "
                  required
                />
                <!-- Focus Glow Effect -->
                <div
                  class="absolute inset-0 rounded-2xl ring-1 ring-emerald-500/0 peer-focus:ring-emerald-500/50 transition-all duration-500 pointer-events-none"
                ></div>
              </div>
              <p
                v-if="isExistEventName"
                class="text-red-400 text-xs font-medium ml-2 flex items-center gap-1 mt-1"
              >
                <i class="fa-solid fa-circle-exclamation"></i> {{ t("event name already exists") }}
              </p>
            </div>

                <div class="space-y-2">
              <label for="groom" class="text-sm font-medium text-white/80 ml-1">
                {{ t("Event Owner Name") }}
              </label>
              <div class="relative">
                <input
                  id="eventOwnerName"
                  v-model="eventOwnerName"
                  type="text"
                  :placeholder="t('Enter event owner name')"
                  class="glass-input w-full pl-5 pr-4 py-3.5 rounded-2xl outline-none transition-all duration-300"
                />
              </div>
            </div>

            <!-- Metric Fields (Guest, Event, Expense, Price) -->
            <div class="space-y-2">
              <label for="groom" class="text-sm font-medium text-white/80 ml-1">
                {{ t("Groom Name") }}
              </label>
              <div class="relative">
                <input
                  id="groom"
                  v-model="groom"
                  type="text"
                  :placeholder="t('Enter groom name')"
                  class="glass-input w-full pl-5 pr-4 py-3.5 rounded-2xl outline-none transition-all duration-300"
                />
              </div>
            </div>

            <!-- Bride Name -->
            <div class="space-y-2">
              <label for="bride" class="text-sm font-medium text-white/80 ml-1">
                {{ t("Bride Name") }}
              </label>
              <div class="relative">
                <input
                  id="bride"
                  v-model="bride"
                  type="text"
                  :placeholder="t('Enter bride name')"
                  class="glass-input w-full pl-5 pr-4 py-3.5 rounded-2xl outline-none transition-all duration-300"
                />
              </div>
            </div>

            <div class="space-y-2 col-span-1 lg:col-span-2">
              <label
                for="eventDate"
                class="text-sm font-medium text-white/80 ml-1"
              >
                {{ t("Event Date") }}
              </label>

              <DatePicker
                v-model="eventDate"
                class="w-full"
                dateFormat="dd-M-yy"
                selectionMode="range"
                :placeholder="t('Select Date')"
                fluid
                inputId="dd"
              />
            </div>

            <!-- Description -->
            <div class="col-span-1 lg:col-span-2 space-y-2">
              <label
                for="description"
                class="text-sm font-medium text-white/80 ml-1"
              >
                {{ t("Description") }}
              </label>
              <textarea
                id="description"
                v-model="description"
                rows="4"
                :placeholder="t('Enter description...')"
                class="glass-input w-full px-5 py-4 rounded-2xl outline-none resize-none transition-all duration-300"
              ></textarea>
            </div>

            <!-- Status Toggle -->
            <div class="space-y-2 flex flex-col justify-center">
              <label class="text-sm font-medium text-white/80 ml-1"
                >{{ t("Status") }}</label
              >
              <div
                @click="status = !status"
                class="cursor-pointer group flex items-center gap-3 bg-white/5 border border-white/10 px-4 py-2.5 rounded-2xl hover:bg-white/10 transition-all duration-300 w-fit"
              >
                <div
                  class="relative w-12 h-7 rounded-full transition-colors duration-500 ease-in-out"
                  :class="status ? 'bg-emerald-500' : 'bg-white/20'"
                >
                  <div
                    class="absolute top-1 left-1 bg-white w-5 h-5 rounded-full shadow-md transform transition-transform duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)]"
                    :class="status ? 'translate-x-5' : 'translate-x-0'"
                  ></div>
                </div>
                <span
                  class="text-sm font-medium transition-colors"
                  :class="status ? 'text-emerald-400' : 'text-white/50'"
                >
                  {{ status ? t("Active") : t("Inactive") }}
                </span>
              </div>
            </div>
          </div>

          <!-- Actions -->
          <div
            class="flex items-center justify-end gap-4 pt-6 border-t border-white/10 mt-2"
          >
            <button
              type="button"
              @click="handleClose('close')"
              :disabled="loading"
              class="px-6 py-2.5 rounded-xl text-white/60 font-medium hover:bg-white/10 hover:text-white transition-colors"
            >
              {{ t("Cancel") }}
            </button>

            <button
              type="submit"
              class="relative overflow-hidden px-8 py-2.5 rounded-xl font-bold text-white shadow-lg shadow-emerald-500/20 transform active:scale-95 transition-all duration-300 group"
              :class="loading ? 'cursor-not-allowed opacity-80' : ''"
            >
              <div
                class="absolute inset-0 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500 text-white transition-all duration-300 group-hover:scale-105"
              ></div>
              <div class="relative flex items-center gap-2">
                <span
                  v-if="loading"
                  class="animate-spin rounded-full h-4 w-4 border-b-2 border-white"
                ></span>
                <span>{{ isEditDoc ? t("Save Changes") : t("Create Event") }}</span>
              </div>
            </button>
          </div>
        </form>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { serverTimestamp, where } from "firebase/firestore";
import { onMounted, ref, watch, computed } from "vue";
import { projectAuth } from "@/firebase/config";
import useCollection from "@/composables/useDocument";
import { getCollectionQuery } from "@/composables/getCollection";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const props = defineProps({
  visible: Boolean,
  isEditDoc: Boolean,
  doc: Object,
});
const emit = defineEmits(["onClose", "added"]);

const name = ref("");
const originalName = ref(null);
const description = ref("");
const status = ref(true);

const isExistEventName = ref(null);
const loading = ref(false);
const open = ref(false);
const eventDate = ref([]);
const groom = ref("");
const bride = ref("");
const eventOwnerName = ref("");
const timeStamp = ref(serverTimestamp());

// Helper for looping inputs

// Setup Logic
onMounted(async () => {
  if (props.visible) open.value = props.visible;
  initForm();
});

watch(
  () => props.visible,
  (val) => {
    open.value = val;
  }
);

watch(
  () => props.isEditDoc,
  () => initForm()
);
watch(
  () => props.doc,
  () => initForm()
);

function initForm() {
  if (props.isEditDoc && props.doc) {
    name.value = props.doc.eventName || "";
    originalName.value = props.doc.eventName || "";
    eventOwnerName.value = props.doc.eventOwnerName || "";
    groom.value = props.doc.groom || "";
    bride.value = props.doc.bride || "";
    const getJsDate = (val) => val?.toDate ? val.toDate() : new Date(val);

    eventDate.value =
      props.doc.eventStartDate && props.doc.eventEndDate
        ? [getJsDate(props.doc.eventStartDate), getJsDate(props.doc.eventEndDate)]
        : [];
  } else {
    handleClear();
  }
}

watch(name, async(newV) => {
  if (originalName.value && name.value === originalName.value) return;
  if (!newV) isExistEventName.value = null;
  if(newV){
    originalName.value = newV;
     await getCollectionQuery(
          "events",
          [where("eventName", "==", name.value)],
          (data) => {
            if(data.length > 0){
              isExistEventName.value = true;
            }else{
              isExistEventName.value = false;
            }
          },
          null,
          false
        );
  }

  
});

const handleClear = () => {
  name.value = "";
  originalName.value = null;
  isExistEventName.value = null;
  eventOwnerName.value = "";
  description.value = "";
  status.value = true;
  eventDate.value = [];
  groom.value = "";
  bride.value = "";
};

const handleClose = (status) => {
  open.value = false;
  handleClear();
  setTimeout(() => emit("onClose", status), 200);
};

const { addDocs, updateDocs } = useCollection("events");

const handleSubmit = async () => {
  try {
    loading.value = true;
    if (isExistEventName.value) {
      loading.value = false;
      return;
    }
    const requestBody = {
      eventName: name.value,
      userId: projectAuth.currentUser?.uid || "",
      eventStartDate: eventDate.value && eventDate.value.length > 0 ? eventDate.value[0] : null,
      eventEndDate: eventDate.value && eventDate.value.length > 1 ? eventDate.value[1] : null,
      groom: groom.value || "",
      eventOwnerName: eventOwnerName.value || "",
      bride: bride.value || "",
      status: status.value,
      description: description.value || "",
     
    };

    if (!props.isEditDoc) {
      requestBody.createdAt = timeStamp.value;
      requestBody.createdBy = projectAuth.currentUser?.uid || "";
      requestBody.statusString = 'Pending'
      console.log("requestBody", requestBody)
     await addDocs(requestBody);
      handleClose("create");
    } else {
      requestBody.updatedAt = timeStamp.value;
      requestBody.updatedBy = projectAuth.currentUser?.uid || "";
      requestBody.statusString = props.doc.statusString || 'Pending'

      await updateDocs(props.doc.id, requestBody);
      handleClose("update");
    }
  } catch (err) {
    loading.value = false;
    console.error("Error submitting form:", err);
  }
};
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.animate-scale-up {
  animation: scaleUp 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}

@keyframes scaleUp {
  from {
    opacity: 0;
    transform: scale(0.95) translateY(10px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

:deep(.p-datepicker) {
  @apply rounded-2xl border border-white/10 bg-slate-900/90 backdrop-blur-xl shadow-xl;
}

:deep(.p-datepicker-input) {
  @apply w-full bg-white/5 backdrop-blur-xl border border-white/20 text-white placeholder-indigo-200/50 rounded-2xl py-4 pl-12 pr-4 transition-all font-medium;
}

:deep(.p-datepicker-input:focus) {
  @apply outline-none ring-2 ring-indigo-500/50 bg-white/10;
}

/* Calendar Panel Styles */
:deep(.p-datepicker-header) {
  @apply border-b border-white/10 bg-transparent text-white pt-4 pb-2;
}

:deep(.p-datepicker-title) {
  @apply text-white font-bold;
}

:deep(.p-datepicker-prev-icon),
:deep(.p-datepicker-next-icon) {
  @apply text-white/70 hover:text-white transition-colors;
}

:deep(.p-datepicker-table) {
  @apply my-2;
}

:deep(.p-datepicker-table th) {
  @apply text-indigo-300 font-medium p-2;
}

:deep(.p-datepicker-table td) {
  @apply p-1;
}

:deep(.p-datepicker-table td > span) {
  @apply text-white/90 w-8 h-8 rounded-full transition-all border border-transparent;
}

:deep(.p-datepicker-table td > span:hover) {
  @apply bg-white/10 text-white;
}

:deep(.p-datepicker-table td.p-datepicker-today > span) {
  @apply bg-indigo-500/20 text-indigo-300 border-indigo-500/50 font-bold;
}

:deep(.p-datepicker-table td > span.p-highlight) {
  @apply bg-indigo-600 text-white shadow-lg shadow-indigo-600/20;
}

:deep(.p-button-text) {
  @apply text-indigo-300 hover:bg-indigo-500/10 hover:text-indigo-200;
}
</style>